import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, Play } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';
import { IndexPath } from '@/Config/app/router/index.path';
import { KnowledgeBase } from '@/Domain/api/KnowledgeBase/dto/KnowledgeBase.dto';
import { RunListDto } from '@/Domain/api/Simulation/dto/Simulation.dto';
import { Usage } from '@/Domain/api/Subscription/dto/SubscriptionUsage.dto';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/Domain/components/Shared/Dialog/Dialog';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { TierEnum } from '@/Domain/enum/Tier.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useGetSubscription } from '@/Domain/hooks/Subscription/useGetSubscription.hook';

// Matches the backend's DEFAULT_CREDIT_COST; only reached if the rate is missing or unusable.
const DEFAULT_CREDIT_COST_PER_MESSAGE = 1;

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  datasetId: number;
  messageCount: number;
  onSuccess: (runId: number) => void;
};

export const StartRunDialog = ({
  open,
  onOpenChange,
  datasetId,
  messageCount,
  onSuccess,
}: Props) => {
  const { t } = useTranslation('simulation_page');
  const { capture } = usePostHogAnalytics();
  const navigate = useNavigate();
  const { scenario = '' } = useParams<{ scenario: string }>();
  const [label, setLabel] = useState('');

  const { data: usage } = useQuery<Usage>({
    queryKey: [QueryKey.subscription.usage],
    enabled: open,
  });
  const { data: subscription } = useGetSubscription();
  const isCustomTier = subscription?.current_plan.tier === TierEnum.CUSTOM;

  const { data: knowledgeBase } = useQuery<KnowledgeBase>({
    queryKey: [QueryKey.knowledgeBase.get],
    enabled: open,
  });
  const pendingEmbedCount = knowledgeBase?.pending_embed_count ?? 0;
  const failedEmbedCount = knowledgeBase?.failed_embed_count ?? 0;
  // Only atomic agents can be stale here; legacy agents use the retrain pipeline this dialog does not report on.
  const isKbStale =
    knowledgeBase?.atomic_embed_enabled === true && (pendingEmbedCount > 0 || failedEmbedCount > 0);

  const startMutation = useMutation<RunListDto, unknown, { datasetId: number; label?: string }>({
    mutationKey: [QueryKey.simulation.startRun],
  });

  const planRemaining = usage ? Math.max(0, usage.messages_limit - usage.messages_used) : 0;
  const topupCredits = usage?.topup_credits ?? 0;
  const remaining = usage ? planRemaining + topupCredits : null;
  // Balance and rate come from one response, so the two can never disagree about the unit.
  // A pre-deploy pod (or a cache seeded by one) serves no rate at all, and `staleTime: Infinity`
  // would keep that payload all session, so an unusable rate falls back rather than quoting NaN.
  const servedRate = usage?.credit_cost_per_message;
  const creditRate =
    typeof servedRate === 'number' && Number.isFinite(servedRate) && servedRate > 0
      ? servedRate
      : DEFAULT_CREDIT_COST_PER_MESSAGE;
  const creditsNeeded = usage ? messageCount * creditRate : null;
  // Per pool, mirroring the gate: one reply is charged wholly to the plan or wholly to
  // purchased credits, so a remainder smaller than one charge cannot be spent at all.
  const affordableMessages = usage
    ? Math.floor(planRemaining / creditRate) + Math.floor(topupCredits / creditRate)
    : null;
  const insufficientQuota =
    !isCustomTier && affordableMessages !== null && affordableMessages < messageCount;

  const handleSubmit = () => {
    startMutation.mutate(
      { datasetId, label: label.trim() || undefined },
      {
        onSuccess: (data) => {
          capture(PostHogEventEnum.SimulationRunStarted, {
            dataset_id: datasetId,
            run_id: data.id,
            message_count: messageCount,
            has_label: label.trim().length > 0,
          });
          if (isKbStale) {
            capture(PostHogEventEnum.SimulationRunStartedOnStaleKb, {
              dataset_id: datasetId,
              run_id: data.id,
              pending_embed_count: pendingEmbedCount,
              failed_embed_count: failedEmbedCount,
            });
          }
          toast.success(t('toast_run_started'));
          setLabel('');
          onSuccess(data.id);
        },
        onError: () => toast.error(t('toast_run_error')),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('start_run_title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <Input
            data-testid="start-run-label"
            placeholder={t('run_label_placeholder')}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />

          {isKbStale && (
            <div
              data-testid="start-run-kb-stale"
              className="rounded-md border border-warning/30 bg-warning-muted px-3 py-2 text-sm text-warning-foreground"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <div>
                  <p>{t('kb_embedding_warning')}</p>
                  <button
                    className="mt-1 text-sm font-medium underline"
                    onClick={() => navigate(`/i/${scenario}/${IndexPath.knowledgeBase.root}`)}
                  >
                    {t('kb_embedding_view_kb')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Quota info. Both figures come from `usage`, so one null check covers both. */}
          {creditsNeeded !== null && (
            <div
              data-testid="start-run-quota"
              className={`rounded-md px-3 py-2 text-sm ${
                insufficientQuota
                  ? 'border border-destructive/30 bg-destructive-muted text-destructive'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              {insufficientQuota ? (
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <div>
                    <p>
                      {t('quota_exceeded_warning', {
                        needed: creditsNeeded,
                        remaining,
                      })}
                    </p>
                    <button
                      className="mt-1 text-sm font-medium text-destructive underline"
                      onClick={() => navigate(`/i/${scenario}/${IndexPath.subscription}`)}
                    >
                      {t('upgrade_plan')}
                    </button>
                  </div>
                </div>
              ) : isCustomTier ? (
                <p>{t('quota_info_custom', { needed: creditsNeeded })}</p>
              ) : (
                <p>
                  {t('quota_info', {
                    needed: creditsNeeded,
                    remaining,
                  })}
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('cancel')}
          </Button>
          <Button
            data-testid="start-run-submit"
            onClick={handleSubmit}
            disabled={startMutation.isPending || insufficientQuota}
          >
            {startMutation.isPending && <Spinner className="mr-1.5 h-3.5 w-3.5" />}
            <Play className="mr-1.5 h-3.5 w-3.5" />
            {t('start_run')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
